import api from 'config/axiosConfig';
import { CoauthorSuggestion } from "../components/Coauthors/CoauthorSelect";

// Define a union type for all possible entity/actor types
type EntityData = {
    id: number;
    title?: string;
    name?: string;
    username?: string;
    teamname?: string;
    entity_name?: string;
    [key: string]: any; // For other properties
};

export const getEntityDetails = async (entityType: string, entityId: string | number): Promise<any> => {
    try {
        const response = await api.get<EntityData>(`/api/${entityType}s/id/${entityId}`);
        const entity = response.data;
        entity.type = entityType;
        if (entityType === 'impact' || entityType === 'influence' || entityType === 'feedback') {
            entity.entity_name = entity.title;
        } else if (entityType === 'solution') {
            entity.entity_name = entity.name;
        } else if (entityType === 'issue') {
            entity.entity_name = entity.cname;
        }

        return entity;
    } catch (error) {
        console.error(`Error fetching ${entityType} details:`, error);
        return {
            data: { id: Number(entityId) } as EntityData,
            title: 'Unknown entity'
        };
    }
};

export const getActorDetails = async (actorType: string, actorId: string | number): Promise<any> => {
    try {
        const response = await api.get<EntityData>(`/api/${actorType}/id/${actorId}`);
        const actor = response.data;
        if (actorType === 'user') {
            actor.entity_name = actor.username;
        } else if (actorType === 'team') {
            actor.entity_name = actor.teamname;
        }

        return actor;
    } catch (error) {
        console.error(`Error fetching ${actorType} details:`, error);
        return {
            data: { id: Number(actorId) } as EntityData,
            title: 'Unknown actor'
        };
    }
};

/**
 * Retrieve pending coauthor invites for a specific entity
 * 
 * @param entityType - The type of entity (impact, solution, etc.)
 * @param entityId - The ID of the entity
 * @returns Array of pending coauthor objects with details
 */
export const getPendingCoauthorRequests = async (
    entityType: string, 
    entityId: string | number
): Promise<CoauthorSuggestion[]> => {
    try {
        // Get pending coauthor invites
        const response = await api.get(`/api/requests`, {
            params: {
                requestType: "coauthor_invite",
                entityType: entityType,
                entityId: entityId,
                status: "pending"
            }
        });

        // Convert requests to coauthor suggestions with pending status
        const pendingCoauthors: CoauthorSuggestion[] = [];
        
        for (const request of response.data.requests) {
            // Get details about the requestee (user or team)
            const requestee = await getActorDetails(
                request.requestee_type, 
                request.requestee_id
            );
            
            // Create a coauthor suggestion object
            pendingCoauthors.push({
                id: requestee.id,
                type: request.requestee_type,
                name: request.requestee_type === 'user' ? requestee.username : requestee.teamname,
                display_name: requestee.display_name,
                request_id: request.id, // Store request ID for removal actions
                isPending: true
            });
        }

        //console.log('pendingCoauthors', pendingCoauthors);
        
        return pendingCoauthors;
    } catch (error) {
        console.error(`Error fetching pending coauthor requests:`, error);
        return [];
    }
};

/**
 * Determine if current user can view followers of another user
 * 
 * @param profileUser - The profile user object
 * @returns True if the current user can view the followers, false otherwise
 */
export const canViewUserFollow = async (profileUser: any): Promise<boolean> => {
    if (profileUser?.properties_public?.is_private) {
        return profileUser?.viewer_relation && profileUser?.viewer_relation?.match(/follower|bestea|admin|self/) ? true : false;
    } else {
        return true;
    }
}

/**
 * Determine if current user can view followers and team members of a partea
 * @param profileTeam: any
 * @returns True if the current user can view the followers and team members, false otherwise
 */
export const canViewTeamFollow = async (profileTeam: any): Promise<boolean> => {
    if (profileTeam?.properties_public?.is_private) {
        return profileTeam?.viewer_relation && profileTeam?.viewer_relation?.match(/admin|creator|moderator|member|follower|bestea/) ? true : false;
    } else {
        return true;
    }
}
